import { monotonicFactory } from 'ulid';

export const INTAKE_ATTACHMENT_NAME_DELIMITER = '__';

const stripFileName = (filename: string) => {
  const trimmed = filename.trim();
  return trimmed.includes('.')
    ? trimmed.slice(0, trimmed.lastIndexOf('.'))
    : trimmed;
};

const sanitizeAttachmentNameSegment = (value: string) => {
  return value
    .replace(/[\\/]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
};

const getNormalizedExtension = (filename: string) => {
  const trimmed = filename.trim();
  const extension = trimmed.includes('.') ? trimmed.split('.').pop() || '' : '';
  return extension.replace(/[^a-zA-Z0-9]+/g, '').toLowerCase();
};

export const buildIntakeAttachmentPrefix = ({
  siteID,
  providerID,
  intakeItemID,
}: {
  siteID: string;
  providerID: string;
  intakeItemID: string;
}) => `site/${siteID}/provider/${providerID}/${intakeItemID}`;

export const buildIntakeAttachmentKey = ({
  siteID,
  providerID,
  intakeItemID,
  fileName,
  nextID,
}: {
  siteID: string;
  providerID: string;
  intakeItemID: string;
  fileName: string;
  nextID: () => string;
}) => {
  const prefix = buildIntakeAttachmentPrefix({
    siteID,
    providerID,
    intakeItemID,
  });
  const extension = getNormalizedExtension(fileName);
  const strippedFileName = stripFileName(fileName);
  const sanitizedFileName = sanitizeAttachmentNameSegment(strippedFileName);
  const ulid = nextID();
  const keyBase = sanitizedFileName
    ? `${prefix}/${ulid}${INTAKE_ATTACHMENT_NAME_DELIMITER}${sanitizedFileName}`
    : `${prefix}/${ulid}`;

  return extension ? `${keyBase}.${extension}` : keyBase;
};

export const buildIntakeAttachmentKeys = ({
  files,
  siteID,
  providerID,
  intakeItemID,
}: {
  files: Array<{ name: string }>;
  siteID: string;
  providerID: string;
  intakeItemID: string;
}) => {
  const nextID = monotonicFactory();

  return files.map((file) =>
    buildIntakeAttachmentKey({
      siteID,
      providerID,
      intakeItemID,
      fileName: file.name,
      nextID,
    })
  );
};

export const getDisplayNameFromStoredAttachmentName = (fileName: string) => {
  const trimmed = fileName.trim();
  if (!trimmed) return '';

  const delimiterIndex = trimmed.indexOf(INTAKE_ATTACHMENT_NAME_DELIMITER);
  if (delimiterIndex < 0) {
    return trimmed;
  }

  const displayName = trimmed.slice(
    delimiterIndex + INTAKE_ATTACHMENT_NAME_DELIMITER.length
  );
  return displayName || trimmed;
};
